import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { customElement, html, css, cssManager, property, state, DeesElement } from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { demoFunc } from './dees-storage-browser.demo.js';
import type { IStorageDataProvider, IStorageChangeEvent, IStorageMutationEvent } from './interfaces.js';
import { getParentPrefix } from './utilities.js';
import './dees-storage-columns.js';
import './dees-storage-keys.js';
import { DeesStoragePreview } from './dees-storage-preview.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-storage-browser': DeesStorageBrowser;
  }
}

type TViewType = 'columns' | 'keys';

@customElement('dees-storage-browser')
export class DeesStorageBrowser extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Data View'];

  @property({ type: Object })
  public accessor dataProvider: IStorageDataProvider | null = null;

  @property({ type: String })
  public accessor bucketName: string = '';

  /**
   * Optional change stream subscription.
   * Pass a function that takes a callback and returns an unsubscribe function.
   */
  @property({ type: Object })
  public accessor onChangeEvent: ((callback: (event: IStorageChangeEvent) => void) => (() => void)) | null = null;

  @state()
  private accessor viewType: TViewType = 'columns';

  @state()
  private accessor currentPrefix: string = '';

  @state()
  private accessor selectedKey: string = '';

  @state()
  private accessor refreshKey: number = 0;

  @state()
  private accessor previewWidth: number = 420;

  @state()
  private accessor isResizingPreview: boolean = false;

  @state()
  private accessor recentChangeCount: number = 0;

  @state()
  private accessor isStreamConnected: boolean = false;

  private changeUnsubscribe: (() => void) | null = null;

  public static styles = [
    cssManager.defaultStyles,
    themeDefaultStyles,
    css`
      :host { display: block; height: 100%; min-width: 0; container: storage / inline-size; font: 13px/1.5 var(--dees-font-family); color: var(--dees-color-text-primary); }
      .browser-container { display: flex; flex-direction: column; height: 100%; border: 1px solid var(--dees-color-border-default); border-radius: var(--dees-radius-xl); background: var(--dees-color-bg-primary); overflow: hidden; }
      .toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 12px; min-height: 48px; padding: 6px 12px; background: var(--dees-color-bg-secondary); border-bottom: 1px solid var(--dees-color-border-subtle); }
      button { font: inherit; color: inherit; cursor: pointer; }
      button:focus-visible, .resize-divider:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px; }
      .breadcrumb { display: flex; align-items: center; gap: 2px; flex: 1; min-width: 80px; overflow-x: auto; color: var(--dees-color-text-secondary); }
      .breadcrumb-item { flex-shrink: 0; padding: 4px 8px; border: 0; border-radius: var(--dees-radius-md); background: transparent; white-space: nowrap; }
      .breadcrumb-item:hover { background: var(--dees-color-hover); color: var(--dees-color-text-primary); }
      .breadcrumb-separator { color: var(--dees-color-text-muted); }
      .content { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1fr); overflow: hidden; }
      .content.has-preview { grid-template-columns: minmax(220px, 1fr) 4px minmax(280px, min(var(--preview-width, 420px), 55%)); }
      .resize-divider { background: var(--dees-color-border-subtle); cursor: col-resize; touch-action: none; }
      .resize-divider:hover, .resize-divider.active { background: var(--dees-color-accent-primary); }
      .main-view { overflow: hidden; min-width: 0; min-height: 0; }
      .preview-panel { display: flex; flex-direction: column; min-width: 0; min-height: 0; background: var(--dees-color-bg-primary); overflow: hidden; }
      .preview-panel dees-storage-preview { flex: 1; min-height: 0; }
      .back-button { display: inline-flex; align-items: center; gap: 6px; min-height: 28px; padding: 4px 6px; border: 0; border-radius: var(--dees-radius-md); background: transparent; }
      .narrow-label { display: none; }
      .back-button:hover { background: var(--dees-color-hover); }
      .stream-status { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--dees-color-text-secondary); }
      .stream-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--dees-color-text-muted); }
      .stream-dot.connected { background: var(--dees-color-text-success); }
      .change-indicator { font-size: 11px; color: var(--dees-color-text-secondary); }
      @container storage (max-width: 800px) {
        .narrow-label { display: inline; } .wide-label { display: none; }
        .content.has-preview { grid-template-columns: minmax(0, 1fr); }
        .has-preview .main-view, .resize-divider { display: none; }
      }
      @media (pointer: coarse) { button { min-height: 44px; } .toolbar { padding: 6px 8px; } }
    `,
  ];

  async connectedCallback() {
    super.connectedCallback();
    this.subscribeToChanges();
  }

  async disconnectedCallback() {
    this.unsubscribeFromChanges();
    this.endPreviewResize();
    await super.disconnectedCallback();
  }

  /**
   * Public method to trigger a refresh of child components
   */
  public refresh() {
    this.refreshKey++;
  }

  /** Snapshot of the current folder and preview, suitable for an app route. */
  public getLocation(): { prefix: string; key: string } { return { prefix: this.currentPrefix, key: this.selectedKey }; }

  /** Apply a host-owned location. Returns false while a draft or write prevents navigation. */
  public setLocation(location: { prefix: string; key?: string }): boolean {
    const key = location.key || '';
    if (location.prefix === this.currentPrefix && key === this.selectedKey) return true;
    if (!this.canLeave()) return false;
    this.currentPrefix = location.prefix;
    this.selectedKey = key;
    return true;
  }

  public canLeave(): boolean { return this.canLeavePreview(); }

  private emitLocationChange() {
    this.dispatchEvent(new CustomEvent('storage-location-change', { detail: this.getLocation(), bubbles: true, composed: true }));
  }

  private setViewType(type: TViewType) {
    this.viewType = type;
  }

  private navigateToPrefix(prefix: string) {
    if (!this.canLeavePreview()) return;
    this.currentPrefix = prefix;
    this.selectedKey = '';
    this.emitLocationChange();
  }

  private handleKeySelected(e: CustomEvent) {
    if (!this.canLeavePreview()) { e.preventDefault(); return; }
    const source = e.composedPath().find(node => node instanceof HTMLElement && node.shadowRoot?.activeElement instanceof HTMLButtonElement) as HTMLElement | undefined;
    this.previewOpener = source?.shadowRoot?.activeElement as HTMLButtonElement | undefined;
    this.selectedKey = e.detail.key;
    if (typeof e.detail.prefix === 'string') this.currentPrefix = e.detail.prefix;
    this.emitLocationChange();
    this.updateComplete.then(() => {
      const list = this.shadowRoot?.querySelector<HTMLElement>('.main-view');
      if (list && getComputedStyle(list).display === 'none') this.shadowRoot?.querySelector<HTMLButtonElement>('.back-button')?.focus();
    });
  }

  private handleNavigate(e: CustomEvent) {
    if (!this.canLeavePreview()) { e.preventDefault(); return; }
    this.currentPrefix = e.detail.prefix;
    this.selectedKey = '';
    this.emitLocationChange();
  }

  private handleObjectDeleted(e: CustomEvent) {
    this.selectedKey = '';
    this.emitLocationChange();
    this.refreshKey++;
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('bucketName') || changedProperties.has('dataProvider')) {
      this.selectedKey = '';
      this.currentPrefix = '';
      this.recentChangeCount = 0;
    }
    if (changedProperties.has('onChangeEvent') || changedProperties.has('bucketName')) {
      this.unsubscribeFromChanges();
      this.subscribeToChanges();
    }
  }

  private subscribeToChanges() {
    if (!this.isConnected || !this.onChangeEvent || this.changeUnsubscribe) {
      this.isStreamConnected = false;
      return;
    }

    try {
      this.changeUnsubscribe = this.onChangeEvent((event: IStorageChangeEvent) => {
        this.handleChange(event);
      });
      this.isStreamConnected = true;
    } catch (error) {
      console.warn('[StorageBrowser] Failed to subscribe to changes:', error);
      this.isStreamConnected = false;
    }
  }

  private unsubscribeFromChanges() {
    if (this.changeUnsubscribe) {
      this.changeUnsubscribe();
      this.changeUnsubscribe = null;
    }
    this.isStreamConnected = false;
  }

  private handleChange(event: IStorageChangeEvent) {
    if (event.bucket !== this.bucketName) return;
    this.recentChangeCount++;
    this.refreshKey++;
  }

  private mutationAffects(key: string, event: IStorageMutationEvent): boolean {
    return !!key && (event.isFolder ? key.startsWith(event.sourceKey) : key === event.sourceKey);
  }

  private beforeMutation(event: CustomEvent<IStorageMutationEvent>) {
    const mutation = event.detail;
    if (mutation.bucket === this.bucketName && this.mutationAffects(this.selectedKey, mutation) && !this.canLeavePreview()) event.preventDefault();
  }

  private handleMutation(event: CustomEvent<IStorageMutationEvent>) {
    const mutation = event.detail;
    if (mutation.bucket !== this.bucketName) return;
    const relocate = (key: string) => mutation.destinationKey + key.slice(mutation.sourceKey.length);
    if (this.mutationAffects(this.selectedKey, mutation)) this.selectedKey = mutation.type === 'move' ? relocate(this.selectedKey) : '';
    if (mutation.isFolder && this.mutationAffects(this.currentPrefix, mutation)) this.currentPrefix = mutation.type === 'move' ? relocate(this.currentPrefix) : getParentPrefix(mutation.sourceKey);
    this.emitLocationChange();
  }

  private previewOpener?: HTMLButtonElement;

  private canLeavePreview(): boolean {
    return this.shadowRoot?.querySelector<DeesStoragePreview>('dees-storage-preview')?.canLeave() ?? true;
  }

  private async closePreview() {
    if (!this.canLeavePreview()) return;
    this.selectedKey = '';
    this.emitLocationChange();
    await this.updateComplete;
    if (this.previewOpener?.isConnected) this.previewOpener.focus();
    else this.shadowRoot?.querySelector<HTMLButtonElement>('.breadcrumb-item')?.focus();
  }

  private startPreviewResize = (event: PointerEvent) => {
    event.preventDefault();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    this.isResizingPreview = true;
  };

  private handlePreviewResize = (event: PointerEvent) => {
    if (!this.isResizingPreview) return;
    const bounds = this.shadowRoot!.querySelector('.content')!.getBoundingClientRect();
    this.previewWidth = Math.max(280, Math.min(bounds.right - event.clientX, bounds.width - 224));
  };

  private endPreviewResize = () => { this.isResizingPreview = false; };

  private resizePreviewKey(event: KeyboardEvent) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const bounds = this.shadowRoot!.querySelector('.content')!.getBoundingClientRect();
    const actual = this.shadowRoot!.querySelector('.preview-panel')!.getBoundingClientRect().width;
    this.previewWidth = event.key === 'Home' ? 280 : event.key === 'End' ? bounds.width - 224 : Math.max(280, Math.min(actual + (event.key === 'ArrowLeft' ? 24 : -24), bounds.width - 224));
  }

  render() {
    const breadcrumbParts = this.currentPrefix
      ? this.currentPrefix.split('/').filter(Boolean)
      : [];

    return html`
      <div class="browser-container" @storage-before-mutation=${this.beforeMutation} @storage-mutation=${this.handleMutation}>
        <div class="toolbar">
          <nav class="breadcrumb" aria-label="Storage path">
            <button type="button"
              class="breadcrumb-item"
              @click=${() => this.navigateToPrefix('')}
            >
              ${this.bucketName || 'Files'}
            </button>
            ${breadcrumbParts.map((part, index) => {
              const prefix = breadcrumbParts.slice(0, index + 1).join('/') + '/';
              return html`
                <span class="breadcrumb-separator">/</span>
                <button type="button"
                  class="breadcrumb-item"
                  @click=${() => this.navigateToPrefix(prefix)}
                >
                  ${part}
                </button>
              `;
            })}
          </nav>

          ${this.onChangeEvent ? html`
            <div class="stream-status">
              <span class="stream-dot ${this.isStreamConnected ? 'connected' : ''}"></span>
              ${this.isStreamConnected ? 'Live' : 'Offline'}
            </div>
          ` : ''}
          ${this.recentChangeCount > 0
            ? html`
                <div class="change-indicator pulse">
                  ${this.recentChangeCount} change${this.recentChangeCount > 1 ? 's' : ''}
                </div>
              `
            : ''}
          <dees-input-multitoggle size="sm" label="File view" .labelPosition=${'none'}
            .options=${[{ key: 'columns', label: 'Columns' }, { key: 'keys', label: 'List' }]}
            .selectedOption=${this.viewType} @input=${(event: Event) => event.stopPropagation()}
                    @change=${(event: CustomEvent) => { event.stopPropagation(); this.setViewType(event.detail.value); }}></dees-input-multitoggle>
        </div>

        <div class="content ${this.selectedKey ? 'has-preview' : ''}" style="--preview-width: ${this.previewWidth}px">
          <div class="main-view">
            ${this.viewType === 'columns'
              ? html`
                  <dees-storage-columns
                    .dataProvider=${this.dataProvider}
                    .bucketName=${this.bucketName}
                    .currentPrefix=${this.currentPrefix}
                    .selectedKey=${this.selectedKey}
                    .refreshKey=${this.refreshKey}
                    @key-selected=${this.handleKeySelected}
                    @navigate=${this.handleNavigate}
                  ></dees-storage-columns>
                `
              : html`
                  <dees-storage-keys
                    .dataProvider=${this.dataProvider}
                    .bucketName=${this.bucketName}
                    .currentPrefix=${this.currentPrefix}
                    .selectedKey=${this.selectedKey}
                    .refreshKey=${this.refreshKey}
                    @key-selected=${this.handleKeySelected}
                    @navigate=${this.handleNavigate}
                  ></dees-storage-keys>
                `}
          </div>

          ${this.selectedKey
            ? html`
                <div
                  class="resize-divider ${this.isResizingPreview ? 'active' : ''}"
                  role="separator" tabindex="0" aria-label="Preview width" aria-orientation="vertical" aria-valuemin="280" aria-valuemax=${Math.max(280, this.clientWidth - 224)} aria-valuenow=${Math.round(this.previewWidth)}
                  @pointerdown=${this.startPreviewResize} @pointermove=${this.handlePreviewResize} @pointerup=${this.endPreviewResize} @lostpointercapture=${this.endPreviewResize} @keydown=${this.resizePreviewKey}
                ></div>
                <div class="preview-panel">
                  <dees-storage-preview
                    .dataProvider=${this.dataProvider}
                    .bucketName=${this.bucketName}
                    .objectKey=${this.selectedKey}
                    @object-deleted=${this.handleObjectDeleted}
                  ><button slot="header-actions" type="button" class="back-button" aria-label="Close preview" @click=${this.closePreview}><span class="narrow-label">← Files</span><dees-icon class="wide-label" icon="lucide:X"></dees-icon></button></dees-storage-preview>
                </div>
              `
            : ''}
        </div>
      </div>
    `;
  }
}
